import { useEffect, useRef, useState } from "react";

import { useWsSubscribe } from "@/ws/WebSocketProvider";
import { Input } from "@/components/ui";

export interface WatchSymbol {
  symbol: string;
  asset_class: string;
}

interface Quote {
  bid: number;
  ask: number;
  last: number;
  prevLast: number;
  ts: number;
  pendingTick: 0 | 1 | -1;
}

const DEFAULT_LIST: WatchSymbol[] = [
  { symbol: "AAPL", asset_class: "EQUITY" },
  { symbol: "MSFT", asset_class: "EQUITY" },
  { symbol: "EUR/USD", asset_class: "FX" },
  { symbol: "GBP/JPY", asset_class: "FX" },
  { symbol: "BTC/USDT", asset_class: "CRYPTO" },
  { symbol: "ETH/USDT", asset_class: "CRYPTO" },
];

const STORAGE_KEY = "piggy.terminal.watchlist.v1";

export function WatchlistPane({
  selected,
  onSelect,
}: {
  selected: WatchSymbol;
  onSelect: (s: WatchSymbol) => void;
}) {
  const [list, setList] = useState<WatchSymbol[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as WatchSymbol[];
    } catch {
      /* ignore */
    }
    return DEFAULT_LIST;
  });
  const [filter, setFilter] = useState("");
  const [quotes, setQuotes] = useState<Record<string, Quote>>({});
  const tickClearTimers = useRef<Record<string, number>>({});

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      /* ignore */
    }
  }, [list]);

  function applyTick(msg: Record<string, unknown>) {
    const s = String(msg.symbol ?? "");
    if (!s) return;
    const bid = Number(msg.bid ?? 0);
    const ask = Number(msg.ask ?? 0);
    const last = bid && ask ? (bid + ask) / 2 : Number(msg.price ?? 0);
    setQuotes((prev) => {
      const cur = prev[s];
      const dir: Quote["pendingTick"] = cur ? (last > cur.last ? 1 : last < cur.last ? -1 : 0) : 0;
      return {
        ...prev,
        [s]: {
          bid,
          ask,
          last,
          prevLast: cur?.last ?? last,
          ts: Number(msg.timestamp ?? msg.ts ?? Date.now() / 1000),
          pendingTick: dir,
        },
      };
    });
    if (tickClearTimers.current[s]) window.clearTimeout(tickClearTimers.current[s]);
    tickClearTimers.current[s] = window.setTimeout(() => {
      setQuotes((prev) => (prev[s] ? { ...prev, [s]: { ...prev[s], pendingTick: 0 } } : prev));
    }, 300);
  }

  // `client_tick` is the broker-pivot per-org markup-applied feed; `tick`
  // is the legacy paper-mode raw-feed stream. Subscribing to both lets
  // the watchlist work under either deployment mode.
  useWsSubscribe("client_tick", applyTick);
  useWsSubscribe("tick", applyTick);

  const filtered = list.filter((s) => s.symbol.toLowerCase().includes(filter.toLowerCase()));

  function addSymbol() {
    const sym = filter.trim().toUpperCase();
    if (!sym || list.some((s) => s.symbol === sym)) return;
    setList((cur) => [...cur, { symbol: sym, asset_class: guessAssetClass(sym) }]);
    setFilter("");
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-term-border bg-term-bg p-1.5">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter or add (Enter)"
          onKeyDown={(e) => {
            if (e.key === "Enter") addSymbol();
          }}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-2xs">
          <thead className="sticky top-0 bg-term-bg text-term-mute">
            <tr>
              <th className="px-2 py-1 text-left font-medium uppercase tracking-wider">Sym</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Bid</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Ask</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Last</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => {
              const q = quotes[s.symbol];
              const isSel = selected.symbol === s.symbol;
              const tone = q ? (q.last >= q.prevLast ? "up" : "down") : "";
              return (
                <tr
                  key={s.symbol}
                  onClick={() => onSelect(s)}
                  className={`cursor-pointer border-t border-term-border ${
                    isSel ? "bg-term-elev" : "hover:bg-term-elev"
                  }`}
                >
                  <td className="px-2 py-1 font-medium text-term-fg">{s.symbol}</td>
                  <td className="px-2 py-1 text-right tnum text-term-dim">
                    {q ? q.bid.toFixed(symPrecision(s.symbol)) : "—"}
                  </td>
                  <td className="px-2 py-1 text-right tnum text-term-dim">
                    {q ? q.ask.toFixed(symPrecision(s.symbol)) : "—"}
                  </td>
                  <td
                    key={q ? `${q.ts}-${q.last}` : `idle-${s.symbol}`}
                    data-tick={q?.pendingTick === 1 ? "up" : q?.pendingTick === -1 ? "down" : undefined}
                    className={`px-2 py-1 text-right tnum ${tone}`}
                  >
                    {q ? q.last.toFixed(symPrecision(s.symbol)) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function symPrecision(sym: string): number {
  if (sym.includes("/")) {
    if (sym.endsWith("JPY")) return 3;
    if (sym.includes("BTC") || sym.includes("ETH")) return 2;
    return 5;
  }
  return 2;
}

function guessAssetClass(sym: string): string {
  if (sym.includes("/")) return sym.includes("BTC") || sym.includes("ETH") || sym.includes("USDT") ? "CRYPTO" : "FX";
  return "EQUITY";
}
